import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'

import { cn } from '../lib/cn'
import { useAuth } from '../lib/auth'
import { useT } from '../lib/i18n'
import type { SimpleTKey } from '../lib/i18n'
import { useFocusTrap, useScrollLock } from '../hooks/useFocusTrap'
import { modifierLabel } from '../hooks/useHotkey'
import { Icon } from '../components/ui/Icon'
import type { IconName } from '../components/ui/Icon'
import { BrandAvatar } from '../components/domain/BrandAvatar'
import { visibleNavItems } from './nav'

type CommandGroup = 'brands' | 'goto' | 'actions'

/**
 * The group ids are stable; their headings are looked up here. An explicit map rather than a
 * key built from the id, so the unused-key scan can see every one of them.
 */
const GROUP_KEY: Readonly<Record<CommandGroup, SimpleTKey>> = {
  brands: 'common.nav.brands',
  goto: 'shell.commandPalette.groupGoTo',
  actions: 'shell.commandPalette.groupActions',
}

const GROUP_ORDER: readonly CommandGroup[] = ['brands', 'goto', 'actions']

interface Command {
  id: string
  group: CommandGroup
  label: string
  hint?: string
  icon?: IconName
  leading?: ReactNode
  run: () => void
}

export interface CommandPaletteProps {
  open: boolean
  onClose: () => void
}

/**
 * ⌘K. Switch brand, jump to a screen, run an action — three groups, one list, no submenus.
 * The fastest path through the product for anyone who works in it all day.
 */
export const CommandPalette = ({ open, onClose }: CommandPaletteProps): ReactElement | null => {
  const t = useT()
  const navigate = useNavigate()
  const { brands, activeBrand, switchBrand, isSuperadmin, logout } = useAuth()
  const panelRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)

  useFocusTrap(panelRef, open)
  useScrollLock(open)

  const commands = useMemo<Command[]>(() => {
    const list: Command[] = []

    for (const brand of brands) {
      list.push({
        id: `brand-${brand.id}`,
        group: 'brands',
        label: brand.name,
        hint: brand.blogId === activeBrand?.blogId ? t('common.status.active') : brand.timezone,
        leading: <BrandAvatar brand={brand} size="xs" ring={false} />,
        run: () => {
          switchBrand(brand.blogId)
        },
      })
    }

    for (const item of visibleNavItems(isSuperadmin)) {
      list.push({
        id: `nav-${item.to}`,
        group: 'goto',
        label: t(item.labelKey),
        icon: item.icon,
        run: () => {
          void navigate(item.to)
        },
      })
    }

    list.push(
      {
        id: 'action-new-post',
        group: 'actions',
        label: t('shell.commandPalette.schedulePost'),
        icon: 'plus',
        run: () => {
          void navigate('/app/planner?compose=1')
        },
      },
      {
        id: 'action-connect',
        group: 'actions',
        label: t('shell.commandPalette.connectNetwork'),
        icon: 'link',
        run: () => {
          void navigate('/app/brands')
        },
      },
      {
        id: 'action-design-system',
        group: 'actions',
        label: t('shell.commandPalette.openDesignSystem'),
        icon: 'palette',
        run: () => {
          void navigate('/system')
        },
      },
      {
        id: 'action-sign-out',
        group: 'actions',
        label: t('common.actions.signOut'),
        icon: 'logout',
        run: () => {
          void (async () => {
            await logout()
            await navigate('/login')
          })()
        },
      },
    )

    return list
  }, [brands, activeBrand, switchBrand, isSuperadmin, navigate, logout, t])

  const results = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return commands
    // The group is matched by its *translated* heading — typing "vai" finds the go-to rows in
    // Italian, exactly as typing "go to" does in English.
    return commands.filter(
      (command) =>
        command.label.toLowerCase().includes(needle) ||
        t(GROUP_KEY[command.group]).toLowerCase().includes(needle),
    )
  }, [commands, query, t])

  useEffect(() => {
    setHighlight(0)
  }, [query, open])

  useEffect(() => {
    if (open) {
      setQuery('')
      window.setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open])

  const runAt = useCallback(
    (index: number) => {
      const command = results[index]
      if (!command) return
      onClose()
      command.run()
    },
    [results, onClose],
  )

  if (!open) return null

  const highlighted = results[highlight]

  return createPortal(
    <div className="fixed inset-0 z-70 flex items-start justify-center p-4 pt-[12vh]">
      <div className="anim-fade absolute inset-0 bg-chrome-900/55" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('common.shell.commandPalette')}
        className="anim-rise relative flex w-full max-w-xl flex-col overflow-hidden rounded-lg border border-hair bg-surface shadow-overlay"
      >
        <div className="flex items-center gap-2 border-b border-hair px-4">
          <Icon name="search" size={16} className="text-ink-faint" />
          <input
            ref={inputRef}
            value={query}
            role="combobox"
            aria-expanded="true"
            aria-controls="command-results"
            aria-activedescendant={highlighted ? `command-${highlighted.id}` : undefined}
            aria-label={t('shell.commandPalette.search')}
            placeholder={t('shell.commandPalette.search')}
            /* 0.9375rem: the palette's query line is a 48 px control, and the scale has no step
               between --t-body (14 px) and --t-h3 (16 px). Same literal as Button/Input `lg`. */
            className="ring-focus-inset h-12 w-full bg-transparent text-[0.9375rem] text-ink outline-none placeholder:text-ink-faint"
            onChange={(event) => {
              setQuery(event.target.value)
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault()
                setHighlight((previous) => (results.length === 0 ? 0 : (previous + 1) % results.length))
              } else if (event.key === 'ArrowUp') {
                event.preventDefault()
                setHighlight((previous) =>
                  results.length === 0 ? 0 : (previous - 1 + results.length) % results.length,
                )
              } else if (event.key === 'Enter') {
                event.preventDefault()
                runAt(highlight)
              } else if (event.key === 'Escape') {
                event.preventDefault()
                onClose()
              }
            }}
          />
          <kbd className="t-mono rounded-sm border border-hair px-1.5 py-0.5 text-ink-mute">esc</kbd>
        </div>

        <div id="command-results" role="listbox" className="scroll-paper max-h-[52vh] overflow-y-auto py-2">
          {results.length === 0 ? (
            <p className="t-small px-4 py-6 text-center text-ink-mute">
              {t('shell.commandPalette.noMatch', { query })}
            </p>
          ) : (
            GROUP_ORDER.map((group) => {
              const inGroup = results.filter((command) => command.group === group)
              if (inGroup.length === 0) return null
              return (
                <div key={group}>
                  <p className="t-label px-4 pt-2 pb-1 text-ink-mute">{t(GROUP_KEY[group])}</p>
                  {inGroup.map((command) => {
                    const index = results.indexOf(command)
                    const active = index === highlight
                    return (
                      <button
                        key={command.id}
                        id={`command-${command.id}`}
                        role="option"
                        aria-selected={active}
                        type="button"
                        onMouseEnter={() => {
                          setHighlight(index)
                        }}
                        onClick={() => {
                          runAt(index)
                        }}
                        className={cn(
                          'flex w-full items-center gap-2.5 px-4 py-2 text-left transition-colors',
                          active ? 'bg-brass-wash' : 'hover:bg-sunken',
                        )}
                      >
                        {command.leading ?? (
                          <Icon
                            name={command.icon ?? 'arrow-right'}
                            size={16}
                            className="text-ink-mute"
                          />
                        )}
                        <span className="t-small min-w-0 flex-1 truncate text-ink">
                          {command.label}
                        </span>
                        {command.hint ? (
                          <span className="t-mono shrink-0 text-ink-mute">{command.hint}</span>
                        ) : null}
                      </button>
                    )
                  })}
                </div>
              )
            })
          )}
        </div>

        <div className="flex items-center justify-between border-t border-hair bg-paper px-4 py-2">
          <span className="t-mono text-ink-mute">{t('shell.commandPalette.hint')}</span>
          <span className="t-mono text-ink-mute">{modifierLabel()} K</span>
        </div>
      </div>
    </div>,
    document.body,
  )
}
